import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { getBook, purchaseBook, initializePayment } from '../utils/api';
import { toast } from 'sonner';

export const BookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ full_name: '', user_email: '' });

  useEffect(() => {
    loadBook();
  }, [id]);

  const loadBook = async () => {
    try {
      const response = await getBook(id);
      setBook(response.data);
    } catch (error) {
      console.error('Error loading book:', error);
      toast.error('Book not found');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    try {
      const purchaseResponse = await purchaseBook({
        book_id: book.id,
        ...formData,
        amount: book.price
      });

      const paymentResponse = await initializePayment({
        email: formData.user_email,
        amount: book.price,
        payment_type: 'book',
        item_id: purchaseResponse.data.id
      });

      window.location.href = paymentResponse.data.authorization_url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Purchase failed. Please try again.');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-xl">Loading...</p></div>;
  if (!book) return <div className="min-h-screen flex items-center justify-center"><p className="text-xl">Book not found</p></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/books')} className="mb-6 hover:bg-gray-100">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Books
        </Button>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="sticky top-24">
              <img 
                src={book.cover_image} 
                alt={book.title}
                className="w-full rounded-2xl shadow-2xl"
              />
              {book.pdf_filename && (
                <div className="mt-4 flex items-center justify-center gap-2 text-green-700 bg-green-50 py-3 rounded-lg">
                  <Download className="h-5 w-5" />
                  <span className="font-semibold">PDF Download Included</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <Badge className="mb-4 text-base px-4 py-1">{book.category}</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" style={{fontFamily: 'Work Sans'}}>{book.title}</h1>
            <p className="text-2xl text-gray-600 mb-6">by <span className="font-semibold text-purple-700">{book.author}</span></p>
            
            <div className="prose prose-lg mb-8">
              <p className="text-gray-700 leading-relaxed">{book.description}</p>
            </div>

            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6 mb-8">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-sm text-gray-600">Price</span>
              </div>
              <div className="text-5xl font-bold text-purple-700 mb-4">₦{book.price.toLocaleString()}</div>
              {book.stock > 0 ? (
                <Badge variant="outline" className="text-green-700 border-green-700">In Stock</Badge>
              ) : (
                <Badge variant="outline" className="text-red-700 border-red-700">Out of Stock</Badge>
              )}
            </div>

            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-6 w-6" />
                  Purchase This Book
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handlePurchase} className="space-y-5">
                  <div>
                    <Label htmlFor="full_name" className="text-base">Full Name *</Label>
                    <Input
                      id="full_name"
                      required
                      className="mt-2 h-12"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="user_email" className="text-base">Email Address *</Label>
                    <Input
                      id="user_email"
                      type="email"
                      required
                      className="mt-2 h-12"
                      placeholder="your@email.com"
                      value={formData.user_email}
                      onChange={(e) => setFormData({...formData, user_email: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full h-14 text-lg font-semibold" size="lg">
                    <Download className="mr-2 h-5 w-5" /> Purchase & Download - ₦{book.price.toLocaleString()}
                  </Button>
                  <p className="text-sm text-gray-500 text-center">
                    Secure payment via Paystack. Download access immediately after payment.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};